import { useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../../components/feature/PageHero';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';
import { useSEOHead } from '../../hooks/useSEOHead';
import ErrorBoundary, { retryLazy } from '../../components/base/ErrorBoundary';

// Lazy load heavy components — retryLazy retries up to 3× on chunk failure
const LoanCalculator         = retryLazy(() => import('./components/LoanCalculator'));
const FinancingTeaserStrip   = retryLazy(() => import('../home/components/FinancingTeaserStrip'));
const FISurvivalGuide        = retryLazy(() => import('./components/FISurvivalGuide'));
const OutOfPocketEstimator   = retryLazy(() => import('./components/OutOfPocketEstimator'));
const ApplicationTransparency = retryLazy(() => import('./components/ApplicationTransparency'));
const CreditUnionVsDealer    = retryLazy(() => import('./components/CreditUnionVsDealer'));
const FirstTimeBuyerGuide    = retryLazy(() => import('./components/FirstTimeBuyerGuide'));
const BadCreditGuide         = retryLazy(() => import('./components/BadCreditGuide'));
const RefinancingGuide       = retryLazy(() => import('./components/RefinancingGuide'));
const FinancingFaq           = retryLazy(() => import('./components/FinancingFaq'));

const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://rockhoundauto.com';

export default function FinancingPage() {
  useSEOHead({
    title: 'Car Financing Warrenton OR | Bad Credit OK | Rockhound',
    description: 'Transparent car financing in Warrenton, OR. Credit union approved dealer. No hidden fees, no dealer markup. All credit types welcome. Get pre-approved today.',
    keywords: 'car financing Warrenton OR, bad credit car loans Oregon, credit union approved dealer, used car financing Oregon Coast',
    canonicalUrl: 'https://rockhoundauto.com/financing',
  });

  useEffect(() => {
    injectFAQSchema(
      'schema-financing-faq',
      pageFaqs.financing,
      'https://rockhoundauto.com/financing#faqpage'
    );

    return () => {
      removeFAQSchema('schema-financing-faq');
    };
  }, []);

  return (
    <>
      <PageHero
        theme="gradient-slate"
        eyebrow={{
          icon: 'ri-bank-line',
          text: 'Credit Union Approved Dealer'
        }}
        title="Car Financing That Actually Works for You"
        subtitle={
          <>
            No hidden fees. No dealer markup. No pressure. Just transparent financing options that fit your budget.{' '}
            <Link to="/inventory" className="text-[#D97706] hover:text-[#b45309] underline">
              Browse our inventory
            </Link>{' '}
            and get pre-approved in minutes.
          </>
        }
        trustSignals={[
          { icon: 'ri-check-line', text: 'All Credit Types Welcome' },
          { icon: 'ri-check-line', text: 'Same-Day Approval' },
          { icon: 'ri-check-line', text: 'No Dealer Markup' }
        ]}
        ctas={[
          { label: 'Get Pre-Approved Now', href: '/apply-for-financing', variant: 'primary' },
          { label: 'Browse Inventory', href: '/inventory', variant: 'secondary' }
        ]}
        stats={[
          { value: '580+', label: 'Minimum Credit Score' },
          { value: '24hrs', label: 'Average Approval Time' },
          { value: '0%', label: 'Dealer Markup' }
        ]}
      >
        <p className="text-sm sm:text-base text-slate-600 mb-8 sm:mb-10">
          Oregon Coast buyers in{' '}
          <Link to="/used-cars-astoria-oregon" className="text-[#D97706] hover:text-[#b45309] underline">
            Astoria
          </Link>{' '}
          and{' '}
          <Link to="/service-areas/seaside" className="text-[#D97706] hover:text-[#b45309] underline">
            Seaside
          </Link>{' '}
          regularly use our financing to drive home the same day.
        </p>
      </PageHero>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <FinancingTeaserStrip />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[500px]" />}>
          <LoanCalculator />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <FISurvivalGuide />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[500px]" />}>
          <OutOfPocketEstimator />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <ApplicationTransparency />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <CreditUnionVsDealer />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <FirstTimeBuyerGuide />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <BadCreditGuide />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <RefinancingGuide />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary fallback={null}>
        <Suspense fallback={<div className="min-h-[600px]" />}>
          <FinancingFaq />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}